import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CardGrid, LoadingState, PageHeader, PageShell, StatCard } from "@/components/app/page";
import { teamAPI } from "@/services/api";
import { PARTNER_LEVELS } from "@/utils/constants";
import { toast } from "sonner";
import { TrendingUp, Users } from "lucide-react";

export default function TeamPage({ user }) {
  const [genealogy, setGenealogy] = useState(null);
  const [members, setMembers] = useState([]);
  const [coachingRows, setCoachingRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadTeamData = useCallback(async () => {
    const [genealogyRes, membersRes, coachingRes] = await Promise.allSettled([
      teamAPI.getGenealogy(),
      teamAPI.getMembers(),
      teamAPI.getDescendantCoaching()
    ]);

    let hasError = false;
    if (genealogyRes.status === "fulfilled") {
      setGenealogy(genealogyRes.value.data);
    } else {
      hasError = true;
      console.error("Error loading genealogy", genealogyRes.reason);
    }

    if (membersRes.status === "fulfilled") {
      setMembers(Array.isArray(membersRes.value.data) ? membersRes.value.data : []);
    } else {
      hasError = true;
      console.error("Error loading team members", membersRes.reason);
    }

    if (coachingRes.status === "fulfilled") {
      setCoachingRows(Array.isArray(coachingRes.value.data) ? coachingRes.value.data : []);
    } else {
      console.error("Error loading descendant coaching", coachingRes.reason);
    }

    if (hasError) {
      toast.error("Certaines donnees de l'équipe n'ont pas pu etre chargees");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadTeamData();
  }, [loadTeamData]);

  const renderTree = (node, level = 0) => {
    if (!node) return null;

    return (
      <div key={node.user.id} className={level > 0 ? "ml-4 mt-4 sm:ml-8" : ""}>
        <Card className="transition-shadow hover:shadow-md">
          <CardContent className="p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#2A4B46] font-semibold text-white">
                  {node.user.full_name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-[#2A4B46]">{node.user.full_name}</p>
                  <p className="break-all text-xs text-[#5C625F]">{node.user.email}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                {node.user.partner_level && (
                  <Badge className={PARTNER_LEVELS[node.user.partner_level]?.color}>
                    {PARTNER_LEVELS[node.user.partner_level]?.label}
                  </Badge>
                )}
                <div className="text-left sm:text-right">
                  <p className="text-xs text-[#5C625F]">MC</p>
                  <p className="font-semibold text-[#2A4B46]">{(node.user.points_monnaie || 0).toFixed(2)}</p>
                </div>
              </div>
            </div>
            {node.user.descendant_count > 0 && (
              <div className="mt-2 text-xs text-[#5C625F]">
                <Users size={12} className="mr-1 inline" />
                {node.user.descendant_count} descendant(s)
              </div>
            )}
          </CardContent>
        </Card>
        {node.children && node.children.length > 0 && (
          <div className="border-l-2 border-[#E6E4E0] pl-3 sm:pl-4">
            {node.children.map((child) => renderTree(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <LoadingState label="Chargement de l'équipe..." />;

  return (
    <PageShell data-testid="team-page">
      <PageHeader title="Mon équipe" description="Arbre genealogique de votre reseau de parrainage" />

      <CardGrid className="xl:grid-cols-2">
        <StatCard title="Total membres" value={members.length} icon={Users} testId="total-members" />
        <StatCard title="Votre niveau" value={PARTNER_LEVELS[user.partner_level]?.label || "P-00"} icon={TrendingUp} tone="pine" />
      </CardGrid>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#2A4B46] font-['Outfit']">Suivi coaching des descendants</CardTitle>
        </CardHeader>
        <CardContent>
          {coachingRows.length === 0 ? (
            <p className="py-6 text-center text-sm text-[#5C625F]">Aucun descendant a suivre pour le moment.</p>
          ) : (
            <div className="space-y-3">
              {coachingRows.map((row) => (
                <div key={row.id} className="rounded-xl border border-[#E6E4E0] bg-white p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-semibold text-[#2A4B46]">{row.full_name}</p>
                      <p className="text-xs text-[#5C625F]">{row.id} | {row.email}</p>
                    </div>
                    <Badge className={row.completed ? "bg-green-100 text-green-800" : "bg-[#F3EFE9] text-[#2A4B46]"}>
                      {row.completed ? "Parcours termine" : `${row.progress?.percent || 0}%`}
                    </Badge>
                  </div>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-[#E6E4E0]">
                    <div className="h-full bg-[#D4A373]" style={{ width: `${Math.min(row.progress?.percent || 0, 100)}%` }} />
                  </div>
                  <p className="mt-2 text-xs text-[#5C625F]">
                    {row.progress?.completed || 0}/{row.progress?.total || 0} etapes completees
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#2A4B46] font-['Outfit']">Arbre genealogique</CardTitle>
        </CardHeader>
        <CardContent>
          {genealogy ? (
            <div data-testid="genealogy-tree">{renderTree(genealogy)}</div>
          ) : (
            <p className="py-8 text-center text-[#5C625F]">Aucune donnee disponible</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#2A4B46] font-['Outfit']">
            Liste des membres ({members.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <p className="py-8 text-center text-[#5C625F]">Aucun membre dans votre équipe</p>
          ) : (
            <div className="space-y-3">
              {members.map((member) => (
                <div
                  key={member.id}
                  className="flex flex-col gap-3 rounded-2xl bg-[#F3EFE9] p-4 sm:flex-row sm:items-center sm:justify-between"
                  data-testid={`member-${member.id}`}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#2A4B46] font-semibold text-white">
                      {member.full_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-[#2A4B46]">{member.full_name}</p>
                      <p className="break-all text-sm text-[#5C625F]">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                    {member.partner_level && (
                      <Badge className={PARTNER_LEVELS[member.partner_level]?.color}>
                        {PARTNER_LEVELS[member.partner_level]?.label}
                      </Badge>
                    )}
                    <div className="text-left sm:text-right">
                      <p className="text-xs text-[#5C625F]">Points</p>
                      <p className="font-semibold text-[#2A4B46]">{(member.points_monnaie || 0).toFixed(2)} MC</p>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="text-xs text-[#5C625F]">Descendants</p>
                      <p className="font-semibold text-[#2A4B46]">{member.descendant_count}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </PageShell>
  );
}
